import type { Task } from 'redux-saga';
import { call, cancel, cancelled, fork, put, select } from 'redux-saga/effects';

import { type GeneratorResult, runSagaAsPromise, takeIf } from '@pressreader/appstore';
import { error } from '@pressreader/logger';
import type { AnyPopup, AnyPopupOptions, IAlertPopup, IDialogPopup, IMenuPopup } from '@pressreader/popups-types';
import { getPopupContent, isDialogPopup } from '@pressreader/ui-popups';
import { newGuid } from '@pressreader/utils';

import type { IPopupResult } from '../types/popup.result';
import type { IPopupSagas } from '../types/popup.sagas';

import { closePopupAction, popupChanged, popupClosed, popupOpened } from './actions';
import { selectPopupById } from './selectors';

function* closePopupById({
    id,
    isCancelled = true,
    result = undefined,
    closeParent = true,
}: {
    id: string;
    closeParent?: boolean;
    isCancelled?: boolean;
    result?: unknown;
}): GeneratorResult<void> {
    const popup: ReturnType<typeof selectPopupById> = yield select(selectPopupById, { id });

    if (!popup) {
        error(`Popup with id ${id} not found.`);
        return;
    }

    const content = isDialogPopup(popup) ? popup.content : null;
    yield put(popupClosed({ id, type: popup.type, content: content ?? null, isCancelled, result }));

    if (popup?.parentId && closeParent) {
        yield put(closePopupAction({ id: popup.parentId, result, isCancelled }));
    }
}

function* closePopup(action: ReturnType<typeof closePopupAction>) {
    const { id, result, isBack, isCancelled } = action.payload;
    yield call(closePopupById, { id, isCancelled, result, closeParent: !isBack });
}

function* closePopupAsCancelled({ id }: { id: string }): GeneratorResult<void> {
    yield call(closePopupById, { id, isCancelled: true, result: undefined, closeParent: true });
}

/**
 * You can use store related API here from global modules only. Don't use selectors and such from your dialog/menu module.
 * Doing so will mess up initialization and tree shaking.
 *
 * A caller that must still be able to close the popup after this saga is gone (a cancelled floating open) names the
 * instance through `id`; everyone else lets it be generated.
 */
function* openPopup<TResultData>(
    popupOptions: AnyPopupOptions,
    sagas?: IPopupSagas<TResultData>,
    id: string = newGuid(),
): GeneratorResult<IPopupResult<TResultData | undefined>> {
    yield put(popupOpened({ popup: { ...popupOptions, id, content: getPopupContent(popupOptions.name) ?? undefined } }));

    let task: Task | undefined;
    let result: IPopupResult<TResultData | undefined>;

    try {
        if (sagas?.onOpened) {
            try {
                task = yield fork(sagas.onOpened, id);
            } catch (e) {
                error(`Error running onOpened saga for ${popupOptions.name}.`, e);
            }
        }

        while (true) {
            const action: ReturnType<closePopupAction<TResultData>> = yield takeIf<ReturnType<closePopupAction<TResultData>>['payload']>(
                closePopupAction,
                a => a.payload.id === id,
            );
            result = { popupId: id, cancelled: action.payload.isCancelled === true, data: action.payload.result, isBack: action.payload.isBack };

            let canClose = true;
            if (sagas?.onClosing) {
                try {
                    canClose = (yield sagas.onClosing(result)) !== false;
                } catch (e) {
                    error(`Error running onClosing saga for ${popupOptions.name}`, e);
                    canClose = false;
                }
            }

            if (canClose) {
                yield closePopup(action);
                break;
            }
        }
    } catch (e) {
        error(e);
        throw e;
    } finally {
        if ((yield cancelled()) && task && sagas?.onClosing) {
            yield call(sagas.onClosing, { popupId: id, cancelled: true, data: undefined, isBack: false } as IPopupResult<TResultData | undefined>);
        }
        if (task) {
            yield cancel(task);
        }
    }

    return result;
}

/**
 * Promise facade over the saga-shaped `openPopup` for saga-free callers (listener effects, async code).
 * Runs the popup saga unchanged as its own root task — its `takeIf(closePopupAction)` loop is the popup's
 * sole close mechanism, so it must keep running for the popup's lifetime. Resolves when the popup closes.
 * Built on `runSagaAsPromise` (unlike the resolve-only alert facades) so a popup-saga error rejects instead
 * of leaving the caller awaiting forever. No `IPopupSagas` support — onOpened/onClosing are saga-coupled;
 * callers needing them keep using `openPopup` from a saga.
 */
function openPopupAsync<TResultData>(popupOptions: AnyPopupOptions): Promise<IPopupResult<TResultData | undefined>> {
    return runSagaAsPromise(() => openPopup<TResultData>(popupOptions));
}

function* updatePopup<TPopup extends AnyPopup>(
    popupId: string,
    changes: Partial<Omit<TPopup, keyof IDialogPopup | keyof IMenuPopup | keyof IAlertPopup>>,
): GeneratorResult<void> {
    const popup: ReturnType<typeof selectPopupById> = yield select(selectPopupById, { id: popupId });
    if (!popup) {
        return;
    }
    yield put(popupChanged({ popup: { ...popup, ...changes } }));
}

export { closePopupAsCancelled, openPopup, openPopupAsync, updatePopup };
